import { useState } from 'react';
import { ErrorBoundary as BaseErrorBoundary } from 'react-error-boundary';

import { IntentionPromptSharedModalConnected } from '@grow-therapy-team/seedling-components';

import { logger } from '../../datadog';
import { useTrackEvent } from '../../segment/segment';
import { EventTypeEnum } from '../../segment/types';
import { useSendSessionIntentionPromptCallback } from '../messages/useSendSessionIntentionPromptCallback';
import { IntentionNotSetModalWrapper } from './IntentionNotSetModalWrapper';
import { IntentionSetModalWrapper } from './IntentionSetModalWrapper';
import { IntentionSettingButtonWrapper as IntentionSettingButton } from './IntentionSettingButtonWrapper';
import { useIntentionState } from './useIntentionState';
import { IntentionUse } from './useIntentionUse';

function ErrorBoundary({ children }: { children: React.ReactNode }) {
  return <BaseErrorBoundary FallbackComponent={() => null}>{children}</BaseErrorBoundary>;
}

const IntentionPromptModal = ({
  isOpen,
  onClose,
  use,
  clientName,
  initials,
  providerImage,
  clientUserShortId,
  providerShortId,
  appointmentShortId,
  onSendPrompt,
  providerCanSetIntentions,
}: {
  isOpen: boolean;
  onClose: () => void;
  use: IntentionUse;
  clientName: string;
  initials: string;
  providerImage: string;
  clientUserShortId: string;
  providerShortId: string;
  appointmentShortId: string;
  onSendPrompt: (appointmentShortId: string) => void;
  providerCanSetIntentions: boolean;
}) => {
  switch (use) {
    case 'not-set':
    case 'not-set-active':
      return (
        <IntentionNotSetModalWrapper
          clientName={clientName}
          isOpen={isOpen}
          onClose={onClose}
          providerImage={providerImage}
          onSendPrompt={() => onSendPrompt(appointmentShortId)}
          clientUserShortId={clientUserShortId}
          appointmentShortId={appointmentShortId}
          providerCanSetIntentions={providerCanSetIntentions}
        />
      );
    case 'shared':
      return (
        <IntentionPromptSharedModalConnected
          isOpen={isOpen}
          onClose={onClose}
          name={clientName}
          providerPhotoUrl={providerImage}
          onResendPrompt={() => onSendPrompt(appointmentShortId)}
          appointmentShortId={appointmentShortId}
        />
      );
    case 'set':
      return (
        <IntentionSetModalWrapper
          isOpen={isOpen}
          onClose={onClose}
          clientName={clientName}
          initials={initials}
          clientUserShortId={clientUserShortId}
          providerShortId={providerShortId}
          appointmentShortId={appointmentShortId}
          providerCanSetIntentions={providerCanSetIntentions}
        />
      );
    default:
      return <></>;
  }
};

export function IntentionSettingWrapper() {
  const [isOpen, setIsOpen] = useState(false);

  const intentionState = useIntentionState();

  const sendSessionIntentionPrompt = useSendSessionIntentionPromptCallback();
  const { userClicked } = useTrackEvent();

  const handleButtonClick = () => {
    userClicked(EventTypeEnum.INTENTION_SETTING_PILL_CLICKED, {
      appointmentShortId,
      intentionState: use,
    });
    setIsOpen(true);
  };

  const handleSendPrompt = async () => {
    try {
      setIsOpen(false);
      await sendSessionIntentionPrompt(1);
    } catch (error) {
      logger.error('Error sending session intention prompt', { error });
    }
  };

  if (!intentionState || intentionState?.isLoading) {
    return null;
  }

  const {
    use,
    appointmentShortId,
    preferredName,
    initials,
    providerImage,
    clientUserShortId,
    providerShortId,
    providerCanSetIntentions,
  } = intentionState;

  return (
    <ErrorBoundary>
      <div className='absolute top-2 left-2'>
        <IntentionPromptModal
          isOpen={isOpen}
          onClose={() => setIsOpen(false)}
          clientName={preferredName}
          initials={initials}
          providerImage={providerImage}
          use={use}
          clientUserShortId={clientUserShortId}
          providerShortId={providerShortId}
          appointmentShortId={appointmentShortId}
          onSendPrompt={handleSendPrompt}
          providerCanSetIntentions={providerCanSetIntentions}
        />
        <IntentionSettingButton
          use={use}
          onClick={handleButtonClick}
          clientName={preferredName}
          appointmentShortId={appointmentShortId}
        />
      </div>
    </ErrorBoundary>
  );
}
